/**
 * Role-aware portal home (Jay 2026-08-17, via Max 2026-08-22).
 *
 * "The center launcher grid gets replaced by a personal dashboard per role... Build it as a general
 * role-to-dashboard framework, not a two-role special case."
 *
 * This component knows nothing about Developers, Designers or sample rounds. It renders whatever cards
 * the server hands it: a label, a status name, and the rows to test against that status. Giving the
 * CEO or Costing a home is then a row in 00_PORTAL_home_cards, not a new screen — which is the whole
 * point of the ask.
 *
 * The one rule it does apply is `roundMatchesStatus`, imported from the Sample Tracker itself. That is
 * the same function behind the tracker's KPI cards and its All Rounds filter, so a card here and the
 * screen it drills into count the same thing. Re-deriving "RM Waiting" in SQL would have been a second
 * definition of it, and this codebase has already paid for that kind of copy more than once.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, AlertTriangle, CalendarDays, Eye, Check } from 'lucide-react';
import { roundMatchesStatus, roundStyleLabel } from './15_SampleTracker';
import { BACKEND_URL } from './backendConfig';
import './HomeDashboard.css';
import DesignerDonut from './DesignerDonut';
import { pickL10n } from './l10nField';

// Most overdue first. D-Day is positive when a round is late (flipped 2026-08-12), and a round with no
// D-Day at all sorts last rather than jumping to the top of a list about what needs attention.
const byAttention = (a, b) => {
  const av = Number.isFinite(a.dDay) ? a.dDay : -Infinity;
  const bv = Number.isFinite(b.dDay) ? b.dDay : -Infinity;
  return bv - av;
};

/* What the card's own link is called. It used to say "Project Tracker" or "Sample Tracker" from a
   flag on the card, which was right while those were the only two destinations — the CEO and Sales
   OP queues also open Project Approval and Pricing Approval, and a footer that names the wrong
   screen is worse than one that names none. */
const DRILL_LABEL = {
  '/development/project-tracker': 'nav.projectTracker',
  '/development/sample-tracker': 'nav.sampleTracker',
  '/development/project-approval': 'nav.projectApproval',
  '/sales/pricing-approval': 'nav.pricingApproval',
};
/* One screen holds both trackers, so the path alone does not name the destination — the TAB does.
   A card that opens the project tab has to say Project Tracker even though its path says sample. */
const drillLabelKey = (card) => {
  if (/tab=project/.test(card.drillQuery || '')) return 'nav.projectTracker';
  if (/tab=sample/.test(card.drillQuery || '')) return 'nav.sampleTracker';
  return DRILL_LABEL[card.drillPath] || (card.isProject ? 'nav.projectTracker' : 'nav.sampleTracker');
};

const HomeDashboard = ({
  data, lang, asRep = '', onAsRep, asUser = '', onAsUser, onChanged, onReload, stacked = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ko = String(lang || '').startsWith('ko');
  // The card's words are kept in the database in all four languages (sql/20261021i); English where one is missing.
  /* Which cards are showing what has already been acknowledged. Per card, because a person may want
     to look back at their dismissed rejections without unpacking everything else on the page. */
  const [showSeen, setShowSeen] = useState({});
  const [acking, setAcking] = useState('');

  /* Jay, 2026-09-14: "once a Sales Rep clicks to confirm they've seen a rejected item, it should stop
     appearing in their Rejected card from then on". The server answers with the whole list again
     rather than a success flag — the row may have moved card, and re-deriving that here would be a
     second copy of a rule that already lives on the server. */
  /* The badly-late threshold, edited where it takes effect.

     `null` means "not edited", and an empty STRING means "the box is genuinely empty". They were the
     same value once, and that is why deleting did not work — Max, 2026-09-16: backspacing "30" down
     to "" made the draft empty, the empty draft fell back to the saved number, and the box refilled
     itself with 30 the moment the last digit went. The only way to clear it was to select the whole
     thing and type over. Two states cannot share one value when the user can reach both. */
  const [daysDraft, setDaysDraft] = useState(null);
  const [daysBusy, setDaysBusy] = useState(false);
  const [daysError, setDaysError] = useState('');
  /* Saved is SHOWN, not assumed — Max, 2026-09-16: "cho tôi trạng thái save khác nhau khi chưa lưu
     và đã lưu". Pressing a button that looks the same before and after tells nobody whether the
     number took. Cleared as soon as the box is edited again, because then it is untrue. */
  const [daysSaved, setDaysSaved] = useState(false);

  const saveDays = async (event) => {
    event.stopPropagation();
    setDaysBusy(true);
    setDaysError('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/portal/home/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sampleLateDays: Number(daysDraft) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setDaysError(d.error || t('xShell.couldNotSave')); return; }
      setDaysDraft(null);
      setDaysSaved(true);
      // Everything in this section counted with the old number; ask the server again rather than
      // patch one card and leave the rest describing a threshold that no longer exists.
      if (onReload) onReload();
    } catch (err) {
      setDaysError(String(err.message || err));
    } finally {
      setDaysBusy(false);
    }
  };

  /* Dirty means "differs from what is stored", not "has been typed in": typing 30 over 30 leaves
     nothing to save, and offering to save it would make the Saved state a lie the next second. */
  const daysDirty = daysDraft !== null && daysDraft.trim() !== String(data.lateDays || '');
  const daysValid = /^\d+$/.test((daysDraft || '').trim())
    && Number(daysDraft) >= 1 && Number(daysDraft) <= 365;

  const acknowledge = (card, row, undo = false) => async (event) => {
    event.stopPropagation();
    if (acking) return;
    setAcking(row.groupId);
    try {
      const r = await fetch(`${BACKEND_URL}/api/portal/home/projects/${encodeURIComponent(row.groupId)}/acknowledge`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardKey: card.cardKey, undo }),
      });
      const d = await r.json();
      if (r.ok && onChanged) onChanged(d.projectRows || []);
    } finally { setAcking(''); }
  };

  const cards = useMemo(() => (data.cards || []).map((card) => {
    /* A projects card carries project groups, whose state the SERVER settled — a group's state has
       never been defined anywhere else, unlike a round's, so there is nothing here to keep in step. */
    /* `queue` (CEO and Sales OP) carries the same row shape as `projects` and is filtered the same
       way — the rows are decisions waiting on somebody rather than a rep's own projects, but the
       screen draws them identically. */
    if (card.source === 'projects' || card.source === 'queue') {
      const all = (data.projectRows || []).filter((row) => row.state === card.statusKey);
      /* Acknowledged rows leave the card and stop being counted — that is the whole ask. They are
         kept on hand rather than dropped so a mis-click is recoverable: the card offers to show them
         again, and each one can be put back. */
      const seen = all.filter((row) => row.acknowledged);
      const rows = all.filter((row) => !row.acknowledged);
      return { ...card, rows, seen, count: rows.length, isProject: true };
    }
    const rows = (data.rows || [])
      .filter((row) => {
        if (!card.picField) return true;
        // The row already came back filtered to this person; this only separates the two PIC columns
        // when one dashboard mixes them.
        const who = card.picField === 'designer' ? row.hincDesigner : row.developer;
        return !data.picName || who === data.picName;
      })
      .filter((row) => roundMatchesStatus(row, card.statusKey))
      .sort(byAttention);
    return { ...card, rows, seen: [], count: rows.length };
  }), [data]);

  const drill = (card) => {
    if (!card.drillPath) return;
    /* Viewing everybody means there is no name to filter by — the card then opens its list unfiltered
       rather than sending `salesRep=` and landing on something that looks filtered but is not. */
    const query = String(card.drillQuery || '')
      .replace('{pic}', encodeURIComponent(data.picName || ''))
      /* The threshold the card counted with, handed to the screen it opens — the tracker has no
         reason to know a settings table exists, and a list filtered by a different number than the
         card counted with is worse than no filter at all. */
      .replace('{lateDays}', encodeURIComponent(String(data.lateDays || '')))
      .split('&').filter((part) => !/=$/.test(part)).join('&');
    navigate(query ? `${card.drillPath}?${query}` : card.drillPath);
  };

  /* ONE ROW, not the card. Clicking a project on the home page opens the tracker on THAT project;
     clicking a round opens the round list on that sample. Only where the target screen can actually
     hold it: the tracker lists approved projects only, which is why the two cards that do not link
     do not make their rows clickable either. */
  /* Which URL parameter carries ONE row of this card, per card. A queue card opens a screen that
     takes a different key depending on what it lists — an approval queue takes a PRJ, the pricing
     queue takes a CP, the tracker takes a SKU or a sample — and a row that cannot name itself to its
     destination is a row that should not look clickable. */
  const QUEUE_ROW_PARAM = {
    project_approval: 'project',
    waiting_approval: 'project',
    rejected: 'project',
    costing_approval: 'open',
    no_sample: 'project',
    past_due: 'project',
    sample_late: 'sample',
    // Stage still Pending on an open SKU (2026-10-15) — a SKU row, opened in the tracker.
    costing_pending: 'project',
    production_pending: 'project',
    shipment_pending: 'project',
  };

  const canOpenRow = (card, row) => {
    if (!card.drillPath) return false;
    if (card.source === 'queue') return !!QUEUE_ROW_PARAM[card.statusKey] && !!row.groupId;
    return !!(card.isProject ? row.groupId : (row.sampleId || row.baseId));
  };

  const openRow = (card, row) => (event) => {
    if (!card.drillPath) return;
    event.stopPropagation();
    /* A queue row keeps the card's own filter and adds itself to it, so the screen opens on the
       list that was counted WITH the clicked row picked out of it — not on that row alone, because
       the next thing somebody does is look at the ones around it. */
    if (card.source === 'queue') {
      const base = String(card.drillQuery || '')
        .replace('{lateDays}', encodeURIComponent(String(data.lateDays || '')))
        .split('&').filter((part) => !/=$/.test(part));
      base.push(`${QUEUE_ROW_PARAM[card.statusKey]}=${encodeURIComponent(row.groupId)}`);
      navigate(`${card.drillPath}?${base.join('&')}`);
      return;
    }
    const key = card.isProject ? 'project' : 'sample';
    const value = card.isProject ? row.groupId : (row.sampleId || row.baseId);
    if (!value) return;
    const tab = card.isProject ? 'project' : 'sample';
    navigate(`/development/sample-tracker?tab=${tab}&${key}=${encodeURIComponent(value)}`);
  };

  return (
    <section className={`hd-wrap${stacked ? ' stacked' : ''}`}>
      <header className="hd-head">
        <h2>{data.viewingAll ? t('home.allOf', { role: data.roleName }) : t('home.myWork', { role: data.roleName })}</h2>
        {!!data.picName && <span className="hd-pic">{data.picName}</span>}
        {/* An admin checking what a rep will see, without asking them to open their laptop. The server
            sends the list only to somebody allowed to use it, so its presence IS the permission. */}
        {/* ONE picker, fed by whichever list this section carries. A queue section (CEO / Sales OP)
            is scoped by PORTAL ACCOUNT and sends `viewers`; a projects section is scoped by SALES REP
            and sends `reps`. Jay, 2026-09-16: "SOP TEAM role has no per person view? It's only for
            Everyone" — the queue sent no list at all, so the control rendered with the one option it
            always has and read as a picker with nothing to pick. */}
        {!!data.mayViewAll && (!!onAsRep || !!onAsUser) && (
          <label className="hd-asrep">
            <Eye size={13} />
            {t('home.viewAs')}
            {(data.viewers || []).length ? (
              <select value={asUser} onChange={(e) => onAsUser(e.target.value)}>
                <option value="">{t('home.viewAll')}</option>
                {data.viewers.map((v) => (
                  <option key={v.userId} value={v.userId}>
                    {/* GROUPS this person answers for — how much of the queue is theirs — not
                        items waiting today. The two are different numbers and the honest one is
                        the one that cannot disagree with the cards below. */}
                    {v.name} ({v.groups}){v.manager && v.team ? ` — ${v.team}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <select value={asRep} onChange={(e) => onAsRep(e.target.value)}>
                <option value="">{t('home.viewAll')}</option>
                {(data.reps || []).map((r) => (
                  <option key={r.salesRepId} value={r.salesRepId}>
                    {r.name} ({r.projects}){r.linked ? '' : ` — ${t('home.repNoAccount')}`}
                  </option>
                ))}
              </select>
            )}
          </label>
        )}
      </header>

      {data.notice === 'unlinked-sales-rep' && !data.mayViewAll && (
        /* Zeros here would read as "you have no projects". The account simply has not been pointed at
           a Sales Rep yet, and only an admin can do that. */
        <p className="hd-notice">
          <AlertTriangle size={15} />
          {t('home.notLinkedToSalesRep')}
        </p>
      )}

      {data.notice === 'unlinked-employee' && (
        /* Zeros would read as "nothing to do this week". They are not the same thing, and the person
           cannot fix it themselves, so it says who can. */
        <p className="hd-notice">
          <AlertTriangle size={15} />
          {t('home.notLinkedToEmployee')}
        </p>
      )}

      {/* ABOVE THE CARDS, because the donut answers "how am I doing" and the cards answer "what is
          waiting" — the first is the thing Jay says designers only learn once a month. It renders
          only where the server sent one, so no other role's dashboard has to know it exists. */}
      {/* `siblings` is how many cards the row below holds. The donut's card has to be exactly one
          of their width, and that width depends on the ITEM COUNT as well as the template —
          `auto-fit` collapses the tracks nothing is in — so the two grids have to agree on both. */}
      <DesignerDonut data={data.donut} ko={ko} viewingAll={data.viewingAll} picName={data.picName}
        siblings={cards.length} />

      <div className="hd-cards">
        {cards.map((card) => (
          <article
            key={card.cardKey}
            className={`hd-card ${card.tone || ''} ${card.count ? '' : 'empty'} ${card.drillPath ? '' : 'flat'}`}
            onClick={() => drill(card)}
            role={card.drillPath ? 'button' : undefined}
            tabIndex={card.drillPath ? 0 : undefined}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drill(card); } }}
            title={card.drillPath ? t('home.openThisList') : undefined}
          >
            <header>
              <span className="hd-card-label">{pickL10n(card, 'label', lang)}</span>
              <span className="hd-card-count">{card.count}</span>
            </header>
            <p className="hd-card-sub">{pickL10n(card, 'sub', lang)}</p>
            {/* The threshold, on the card it governs — Max, 2026-09-15: "tôi cũng chưa hiểu Jay muốn
                gì". Nothing explains a setting like changing it and watching the number above it
                move. Only for somebody who may change it; everyone else sees the plain sub-line. */}
            {card.statusKey === 'sample_late' && !!data.mayEditSettings && (
              /* The KEYBOARD has to be stopped here as well as the mouse — Jay, 2026-09-16: "I
                 cannot edit the duration by number pad but only can do it by clicking arrows."
                 The card is a button: it listens for Enter and Space and opens the Sample Tracker.
                 A keydown inside this box bubbled straight up to it, so typing a number and
                 pressing Enter navigated away carrying the OLD threshold in the URL, and the typed
                 number was gone. The arrows worked because clicking them never involves Enter.
                 Space was the same trap in the middle of a number nobody would type. */
              <div
                className="hd-card-setting"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="presentation"
              >
                <span>{t('home.lateThreshold')}</span>
                <input
                  type="number"
                  min="1"
                  max="365"
                  /* null = untouched, so show what is stored; anything else is what the person has
                     typed, INCLUDING an empty box. */
                  value={daysDraft === null ? String(data.lateDays || '') : daysDraft}
                  onChange={(e) => { setDaysDraft(e.target.value); setDaysSaved(false); setDaysError(''); }}
                  /* Enter saves, which is what pressing Enter in a one-box form is for, and Escape
                     puts back what was stored. Both are handled rather than merely swallowed: a key
                     that does nothing at all is its own small puzzle. */
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!daysBusy && daysDirty && daysValid) saveDays(e);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setDaysDraft(null); setDaysError(''); setDaysSaved(false);
                    }
                  }}
                  disabled={daysBusy}
                  aria-label={t('home.lateThreshold')}
                />
                {/* Three states the person can tell apart: nothing to save, something to save, and
                    saved. The server refuses anything outside 1-365 with a sentence, so the button
                    does not offer to send one. */}
                <button
                  type="button"
                  /* Blue only when there is something that CAN be saved. An empty or out-of-range
                     box is a change too, but offering it in the same colour as a valid one says
                     "press me" about a button that is disabled. */
                  className={daysDirty && daysValid ? 'is-dirty' : (daysSaved && !daysDirty ? 'is-saved' : '')}
                  onClick={saveDays}
                  disabled={daysBusy || !daysDirty || !daysValid}
                >
                  {daysBusy ? '…' : (daysSaved && !daysDirty ? t('home.saved') : t('home.save'))}
                </button>
                {!!daysDirty && !daysValid && (
                  <em className="hd-card-setting-err">{t('home.lateThresholdRange')}</em>
                )}
                {!!daysError && <em className="hd-card-setting-err">{daysError}</em>}
              </div>
            )}
            {!!card.rows.length && (
              <ul className="hd-card-list">
                {card.rows.slice(0, card.listLimit).map((row) => (card.isProject ? (
                  <li
                    key={row.groupId}
                    /* Only when there is somewhere for THIS ROW to go. A queue card links to an
                       approval screen that takes no row id, so its rows are not clickable — drawing
                       them as clickable and then doing nothing is the swallowed click again. */
                    className={canOpenRow(card, row) ? 'hd-row-open' : ''}
                    onClick={canOpenRow(card, row) ? openRow(card, row) : undefined}
                  >
                    <span className="hd-row-id">{row.groupId}</span>
                    <span className="hd-row-name" title={row.rejectionReason || row.name}>
                      {/* Whose project it is, but only when the list is everybody's. */}
                      {data.viewingAll && !!row.repName && <b className="hd-row-rep">{row.repName}</b>}
                      {row.name}
                    </span>
                    {!!row.launchMonth && (
                      <span className="hd-row-when" title={t('home.targetLaunch')}>
                        <CalendarDays size={11} /> {row.launchMonth}
                      </span>
                    )}
                    {/* Only on the person's own page, and only on a card that is telling them
                        something they have to act on. */}
                    {!!data.mayAcknowledge && card.statusKey === 'rejected' && (
                      <button
                        type="button"
                        className="hd-ack"
                        title={t('home.ackTitle')}
                        disabled={acking === row.groupId}
                        onClick={acknowledge(card, row)}
                      >
                        <Check size={11} /> {t('home.ack')}
                      </button>
                    )}
                  </li>
                ) : (
                  <li
                    key={row.sampleRoundId}
                    className={card.drillPath ? 'hd-row-open' : ''}
                    onClick={openRow(card, row)}
                  >
                    <span className="hd-row-id">{row.sampleId || row.baseId}</span>
                    {/* "…_Option: Black" for a round with an option, the tracker's own label (Max, 2026-10-15). */}
                    <span className="hd-row-name" title={roundStyleLabel(row)}>
                      {/* Whose work it is, but only when the list is everybody's. */}
                      {data.viewingAll && !!card.picField && (
                        <b className="hd-row-rep">
                          {card.picField === 'designer' ? row.hincDesigner : row.developer}
                        </b>
                      )}
                      {roundStyleLabel(row)}
                    </span>
                    {Number.isFinite(row.dDay) && row.dDay > 0 && (
                      <span className="hd-row-late">D+{row.dDay}</span>
                    )}
                  </li>
                )))}
                {card.rows.length > card.listLimit && (
                  <li className="hd-row-more">
                    {t('home.andMore', { n: card.rows.length - card.listLimit })}
                  </li>
                )}
              </ul>
            )}
            {/* A mis-click must not lose a rejection for good. The card says how many it is holding
                back and puts them back one at a time. */}
            {!!card.seen.length && (
              <div className="hd-seen">
                <button
                  type="button"
                  className="hd-seen-toggle"
                  onClick={(e) => { e.stopPropagation(); setShowSeen((v) => ({ ...v, [card.cardKey]: !v[card.cardKey] })); }}
                >
                  {t('home.seenCount', { n: card.seen.length })}
                </button>
                {!!showSeen[card.cardKey] && (
                  <ul className="hd-card-list hd-seen-list">
                    {card.seen.map((row) => (
                      <li key={row.groupId}>
                        <span className="hd-row-id">{row.groupId}</span>
                        <span className="hd-row-name" title={row.rejectionReason || row.name}>{row.name}</span>
                        {!!data.mayAcknowledge && (
                          <button
                            type="button"
                            className="hd-ack undo"
                            disabled={acking === row.groupId}
                            onClick={acknowledge(card, row, true)}
                          >
                            {t('home.ackUndo')}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {/* Only a card that leads somewhere says so. A Sales Rep's cards have no drill path —
                the Project Tracker would show them every team's work. */}
            {!!card.drillPath && (
              <footer className="hd-card-open">
                {t(drillLabelKey(card))}
                {' '}<ArrowRight size={13} />
              </footer>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};

export default HomeDashboard;
